import BuildingRatings from "../overview/widgets/BuildingRatings";
import DealMapCard from "../overview/widgets/DealMapCard";
import PortfolioTitle from "./widgets/PortfolioTitle";
import NursingHPPD from "../overview/widgets/NursingHPPD";
import Occupancy from "../overview/widgets/Occupancy";
import GraphQLFinancialWidget from "./widgets/GeneralFinance";
import PortfolioBuildingSummary from "./widgets/PortfolioBuildingSummary";
import { usePortfolioData } from "contexts/PortfolioContext";
import PDFExport from "components/PDFExport";
import { useRef, useState } from "react";
import { Button } from '@aws-amplify/ui-react';
import { useTabs } from "contexts/TabContext";

// Add keyframes for spinner animation
const spinnerStyles = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const PortfolioOverview = () => {
    const {
        currentPortfolioFacility,
        currentPortfolioFacilityName,
        selectedPeriod,
        consolidatedPortfolioData,
        isPortfolioLoading,
        portfolioNames,
        currentPortfolioName,
    } = usePortfolioData();

    const { addTab } = useTabs();
    const contentRef = useRef(null);
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

    const handlePDFStart = () => {
        setIsGeneratingPDF(true);
    };

    const handlePDFEnd = () => {
        setIsGeneratingPDF(false);
    };

    // Check if there's portfolio data to display
    const hasPortfolioData = consolidatedPortfolioData && Object.keys(consolidatedPortfolioData).length > 0;

    // If no portfolio data or still loading, show a loading state
    if (isPortfolioLoading || !hasPortfolioData || !currentPortfolioFacility) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading portfolio data...</p>
                    {portfolioNames.length > 0 && (
                        <p className="text-sm text-gray-500 mt-2">
                            Found {portfolioNames.length} portfolios
                        </p>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="relative">
            {/* Inject spinner animation styles */}
            <style>{spinnerStyles}</style>
            <div className="p-2 flex items-center gap-2">
                <PDFExport
                    contentRef={contentRef}
                    filename={`Portfolio - ${currentPortfolioFacilityName || 'portfolio'} - ${selectedPeriod || 'overview'}.pdf`}
                    onStart={handlePDFStart}
                    onEnd={handlePDFEnd}
                />
                <Button
                    onClick={() => {
                        const base = `/portfolio/${encodeURIComponent(currentPortfolioName || '')}`;
                        const path = currentPortfolioFacilityName
                            ? `${base}/${encodeURIComponent(currentPortfolioFacilityName)}/financial-model`
                            : `${base}/financial-model`;
                        addTab('financial-model', 'Financial Model', path);
                    }}
                    size="small"
                    className="text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md px-3 py-1.5 hover:bg-blue-100 transition-colors"
                >
                    Financial Model
                </Button>
            </div>
            <div ref={contentRef} className="top-[160px] w-full h-fit gap-2 sm:gap-5 grid grid-cols-4 sm:grid md:grid lg:grid xl:grid xl:grid-cols-12 xxl:grid xxl:grid-cols-12 lg:grid-cols-12 md:grid-cols-12 sm:grid-cols-4">
                <div className="order-1 col-span-full pdf-export-header">
                    <PortfolioTitle />
                </div>
                <div className="order-2 col-span-4 lg:col-span-9 xl:col-span-7 xxl:col-span-5 md:col-span-9 lg:!mb-0 pdf-export-card pdf-export-full-width">
                    <GraphQLFinancialWidget />
                </div>
                <div className="order-3 md:order-4 xl:col-span-3 xxl:col-span-2 lg:col-span-4 md:col-span-5 sm:col-span-2 col-span-4 lg:!mb-0 pdf-export-card pdf-export-half-width">
                    <Occupancy />
                </div>
                <div className="order-4 md:order-3 xl:col-span-2 xxl:col-span-2 lg:col-span-3 md:col-span-3 sm:col-span-2 col-span-4 lg:!mb-0 pdf-export-card pdf-export-half-width">
                    <BuildingRatings />
                </div>
                <div className="order-8 col-span-4 xl:col-span-3 xxl:col-span-2 sm:col-span-4 lg:col-span-4 md:col-span-6 lg:!mb-0 pdf-export-card pdf-export-half-width">
                    <NursingHPPD />
                </div>
                <div className="order-9 col-span-full lg:!mb-0 pdf-export-card pdf-export-full-width">
                    <PortfolioBuildingSummary />
                </div>
                <div className="order-10 col-span-full lg:!mb-0 pdf-export-card pdf-export-full-width">
                    <DealMapCard />
                </div>
            </div>

            {/* PDF Generation Overlay */}
            {isGeneratingPDF && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 9999,
                        backgroundColor: 'rgba(0, 0, 0, 0.75)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <div
                        style={{
                            backgroundColor: 'white',
                            padding: '2rem',
                            borderRadius: '0.5rem',
                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                        }}
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <div
                                style={{
                                    width: '3rem',
                                    height: '3rem',
                                    border: '2px solid #e5e7eb',
                                    borderTopColor: '#2563eb',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite',
                                    marginBottom: '1rem'
                                }}
                            ></div>
                            <p style={{ fontSize: '1.125rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                                Generating PDF...
                            </p>
                            <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                Please wait while we prepare your document
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PortfolioOverview; 